import { useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute, useRouter } from '@tanstack/react-router';
import { ArrowLeft, FileText, ListOrdered, Loader2, Paperclip } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { fmt, formatDate } from '@/components/organisms/estimates/support/estimate-ui-helpers';
import { Input } from '@/components/atoms/input';
import { Label } from '@/components/atoms/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/atoms/tabs';
import { Textarea } from '@/components/atoms/textarea';
import { FormPageLayout, DetailPageLayout } from '@/components/layouts';
import { CollapsibleSectionHeader, DetailCard, FormAccordionCard } from '@/components/molecules';
import { TemplateAttachmentsSection } from '@/components/organisms/templates/TemplateAttachmentsSection';
import { TemplateDetailsSection } from '@/components/organisms/templates/TemplateDetailsSection';
import { TemplateEditorRouteBoundary } from '@/components/organisms/templates/TemplateEditorRouteBoundary';
import { TemplateUnsavedChangesDialog } from '@/components/organisms/templates/TemplateUnsavedChangesDialog';
import { DocumentLineItemsCard } from '@/components/organisms/sales/DocumentLineItemsCard';
import { documentLinePickers } from '@/components/organisms/sales/document-line-pickers';
import { lineIdentityKey } from '@/lib/sales/lineIdentity';
import type { EditableLineItem } from '@/routes/_dashboard/dashboard/estimates/_shared';
import {
  SalesDocumentDetailsCard,
  SalesDocumentNotesDocsSection,
} from '@/components/organisms/sales/SalesDocumentDetailSections';
import { useEstimateTemplate } from '@/hooks/estimateTemplates/useEstimateTemplate';
import { useUpdateEstimateTemplate } from '@/hooks/estimateTemplates/useUpdateEstimateTemplate';
import { useEstimateTemplateAttachments } from '@/hooks/estimateTemplates/useEstimateTemplateAttachments';
import {
  tmOverrideUnderquotedServices,
  useEstimateLineItemState,
} from '@/hooks/estimates/useEstimateLineItemState';
import { useLaborRateProfiles } from '@/hooks/pricebook/useDynamicPricing';
import { TemplateActions } from '@/components/organisms/templates/TemplateActions';
import { TemplateMissingPanel } from '@/components/organisms/templates/TemplateMissingPanel';
import { useTemplateEditNavigationGuard } from '@/hooks/useTemplateEditNavigationGuard';
import type { UpdateEstimateTemplateDto } from '@/types/estimateTemplate';
import { TermsSelector } from '@/components/organisms/TermsSelector';
import {
  EMPTY_TERMS_SELECTION,
  readTermsSelection,
  toTermsPayload,
  type TermsSelection,
} from '@/lib/terms';
import {
  hydrateTemplateLineItem,
  templateDefaultsFromTemplate,
  templateDefaultsToDto,
  templateLineItemToDto,
  validateTemplateDefaults,
} from './templateLineItems';
import { useEstimateTemplatePricing } from './useEstimateTemplatePricing';
import {
  EstimateTemplateDefaultsSection,
  EMPTY_TEMPLATE_DEFAULTS,
  type EstimateTemplateDefaultsValue,
} from '@/components/organisms/templates/EstimateTemplateDefaultsSection';

const ESTIMATE_TEMPLATE_TABS = ['overview', 'lineitems', 'notes'] as const;
type EstimateTemplateTab = (typeof ESTIMATE_TEMPLATE_TABS)[number];

export const Route = createFileRoute('/_dashboard/dashboard/settings/templates/estimate/$id')({
  validateSearch: (search): { tab: EstimateTemplateTab; mode?: 'edit' } => ({
    tab: ESTIMATE_TEMPLATE_TABS.includes(search.tab as EstimateTemplateTab)
      ? (search.tab as EstimateTemplateTab)
      : 'overview',
    ...(search.mode === 'edit' ? { mode: 'edit' as const } : {}),
  }),
  component: EstimateTemplateRoutePage,
});

function EstimateTemplateRoutePage() {
  const { id } = Route.useParams();

  return (
    <TemplateEditorRouteBoundary templateId={id}>
      <EstimateTemplatePage />
    </TemplateEditorRouteBoundary>
  );
}

// ── Type grouping ──────────────────────────────────────────────────────────
type LineItemType = 'SERVICE' | 'MATERIAL' | 'EQUIPMENT' | 'LABOR' | 'PACKAGE' | 'OTHER';

const TYPE_META: Record<LineItemType, { label: string; accent: string; badge: string }> = {
  SERVICE: {
    label: 'Services',
    accent: 'border-l-blue-400',
    badge: 'bg-blue-50 text-blue-700 dark:bg-blue-950 dark:text-blue-300',
  },
  MATERIAL: {
    label: 'Materials',
    accent: 'border-l-amber-400',
    badge: 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-300',
  },
  EQUIPMENT: {
    label: 'Equipment',
    accent: 'border-l-purple-400',
    badge: 'bg-purple-50 text-purple-700 dark:bg-purple-950 dark:text-purple-300',
  },
  LABOR: {
    label: 'Labor',
    accent: 'border-l-green-400',
    badge: 'bg-green-50 text-green-700 dark:bg-green-950 dark:text-green-300',
  },
  PACKAGE: {
    label: 'Packages',
    accent: 'border-l-rose-400',
    badge: 'bg-rose-50 text-rose-700 dark:bg-rose-950 dark:text-rose-300',
  },
  OTHER: {
    label: 'Other',
    accent: 'border-l-muted-foreground/30',
    badge: 'bg-muted text-muted-foreground',
  },
};

const TYPE_ORDER: LineItemType[] = [
  'EQUIPMENT',
  'MATERIAL',
  'SERVICE',
  'LABOR',
  'PACKAGE',
  'OTHER',
];

function normalizeType(t: string): LineItemType {
  const upper = t?.toUpperCase() ?? '';
  return upper in TYPE_META ? (upper as LineItemType) : 'OTHER';
}

function estimateTemplateEditSnapshot({
  name,
  description,
  notes,
  terms,
  lineItems,
  defaults,
  businessUnitIds,
}: {
  name: string;
  description: string;
  notes: string;
  terms: TermsSelection;
  lineItems: EditableLineItem[];
  defaults: EstimateTemplateDefaultsValue;
  businessUnitIds: string[];
}) {
  return JSON.stringify({
    name,
    description,
    notes,
    terms,
    businessUnitIds,
    lineItems: lineItems.map((lineItem, index) => templateLineItemToDto(lineItem, index)),
    // Part of the snapshot too: without it, changing only the deposit policy
    // would leave the page believing nothing had changed and let the edit be
    // navigated away from without warning.
    defaults,
  });
}

function EstimateTemplatePage() {
  const router = useRouter();
  const navigate = Route.useNavigate();
  const { id } = Route.useParams();
  const { tab, mode } = Route.useSearch();
  const isEditing = mode === 'edit';
  const enterEdit = () =>
    void navigate({ search: (prev) => ({ ...prev, mode: 'edit' as const }), replace: true });
  const exitEdit = () =>
    void navigate({
      search: (prev) => {
        const { mode: _m, ...rest } = prev;
        return rest;
      },
      replace: true,
    });
  const { data: template, isLoading, isError, error } = useEstimateTemplate(id);
  const updateMutation = useUpdateEstimateTemplate();
  const { data: laborRateProfiles = [] } = useLaborRateProfiles();

  const [showSkeleton, setShowSkeleton] = useState(false);
  useEffect(() => {
    if (!isLoading) return;
    const t = setTimeout(() => setShowSkeleton(true), 150);
    return () => clearTimeout(t);
  }, [isLoading]);

  const [sec, setSec] = useState({
    details: true,
    defaults: false,
    notes: false,
    attachments: false,
  });
  const toggle = (k: keyof typeof sec) => setSec((p) => ({ ...p, [k]: !p[k] }));
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [notes, setNotes] = useState('');
  const [terms, setTerms] = useState<TermsSelection>(EMPTY_TERMS_SELECTION);
  const [defaults, setDefaults] = useState<EstimateTemplateDefaultsValue>(EMPTY_TEMPLATE_DEFAULTS);
  // EMPTY = offered to every business unit (a wildcard), not "none".
  const [businessUnitIds, setBusinessUnitIds] = useState<string[]>([]);
  const [initialized, setInitialized] = useState(false);
  const initialEditSnapshotRef = useRef<string | null>(null);

  // The estimate editor's own line state, so a template is edited with exactly
  // the controls the estimate it produces is edited with.
  const lineState = useEstimateLineItemState();
  const { lineItems, setLineItems } = lineState;

  const { data: attachments = [] } = useEstimateTemplateAttachments(id);

  useEffect(() => {
    if (!template || initialized) return;
    const nextName = template.name;
    const nextDescription = template.description ?? '';
    const nextNotes = template.notes ?? '';
    // A template stores a selection with no snapshot, so read it raw: resolving
    // it here would bake today's default text into the edit form.
    const nextTerms = readTermsSelection(template);
    const items = template.lineItems ?? [];
    const nextLineItems = items.map(hydrateTemplateLineItem);
    const nextDefaults = templateDefaultsFromTemplate(template);
    const nextBusinessUnitIds = template.businessUnitIds ?? [];
    setName(nextName);
    setDescription(nextDescription);
    setNotes(nextNotes);
    setTerms(nextTerms);
    setLineItems(nextLineItems);
    setDefaults(nextDefaults);
    setBusinessUnitIds(nextBusinessUnitIds);
    initialEditSnapshotRef.current = estimateTemplateEditSnapshot({
      name: nextName,
      description: nextDescription,
      notes: nextNotes,
      terms: nextTerms,
      lineItems: nextLineItems,
      defaults: nextDefaults,
      businessUnitIds: nextBusinessUnitIds,
    });
    setInitialized(true);
  }, [template, initialized, setLineItems]);

  function resetForm() {
    if (!template) return;
    setName(template.name);
    setDescription(template.description ?? '');
    setNotes(template.notes ?? '');
    setTerms(readTermsSelection(template));
    const items = template.lineItems ?? [];
    setLineItems(items.map(hydrateTemplateLineItem));
    setDefaults(templateDefaultsFromTemplate(template));
    setBusinessUnitIds(template.businessUnitIds ?? []);
  }

  const currentEditSnapshot = useMemo(
    () =>
      estimateTemplateEditSnapshot({
        name,
        description,
        notes,
        terms,
        lineItems,
        defaults,
        businessUnitIds,
      }),
    [name, description, notes, terms, lineItems, defaults, businessUnitIds],
  );
  const hasUnsavedWork =
    isEditing &&
    initialized &&
    !updateMutation.isPending &&
    initialEditSnapshotRef.current !== currentEditSnapshot;
  const { blocker, allowNextNavigation } = useTemplateEditNavigationGuard(hasUnsavedWork);

  function discardAndExit() {
    allowNextNavigation();
    resetForm();
    exitEdit();
  }

  const { pricingMode, lineItemPreviews, subtotal, taxTotal, grandTotal } =
    useEstimateTemplatePricing(lineItems, defaults);

  /**
   * The defaults as the row STORES them.
   *
   * The standing percent bounds are a rule about input, so they are judged only
   * against a figure this edit is setting — a template saved before the bound
   * existed stays renameable, which is exactly what the server does with it.
   */
  const storedDefaults = useMemo(
    () => (template ? templateDefaultsFromTemplate(template) : undefined),
    [template],
  );

  /**
   * Services this template prices WITHOUT their parts — the shape a template
   * saved before the T&M switch materialized anything still holds, and which
   * every estimate built from it inherits. Named, never repaired on load: the
   * stored line prices are the template's content, and moving them is an edit
   * its author makes.
   */
  const underquotedTMServices = useMemo(
    () =>
      pricingMode === 'TM_OVERRIDE'
        ? tmOverrideUnderquotedServices(lineItems).map((line) => line.name)
        : [],
    [pricingMode, lineItems],
  );

  const handleSave = () => {
    if (!name.trim()) {
      toast.error('Template name is required');
      return;
    }
    if (lineItems.length === 0) {
      toast.error('Add at least one line item');
      return;
    }
    if (lineItems.some((li) => !li.code && !li.name.trim())) {
      toast.error('Name each custom line item');
      return;
    }

    const defaultsError = validateTemplateDefaults(defaults, lineItems, storedDefaults);
    if (defaultsError) {
      toast.error(defaultsError);
      return;
    }

    const dto: UpdateEstimateTemplateDto = {
      name: name.trim(),
      description: description.trim() || undefined,
      notes: notes.trim() || undefined,
      ...toTermsPayload(terms),
      lineItems: lineItems.map((li, index) =>
        templateLineItemToDto(li, index, lineItemPreviews[index]),
      ),
      subtotal,
      taxTotal,
      total: grandTotal,
      businessUnitIds,
      ...templateDefaultsToDto(defaults),
    };

    updateMutation.mutate(
      { id, dto },
      {
        onSuccess: () => {
          initialEditSnapshotRef.current = currentEditSnapshot;
          allowNextNavigation();
          exitEdit();
        },
      },
    );
  };

  if (isLoading) {
    if (!showSkeleton) return null;
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  // A 404 is the only "this template is gone" signal — other failures keep the
  // normal path (and their own toast) rather than claiming a deletion.
  if (isError && error?.status === 404) {
    return <TemplateMissingPanel kind="estimate" />;
  }

  // ── View mode ─────────────────────────────────────────────────────────────
  if (!isEditing) {
    // Priced by the same engine the editor uses, keyed by row so the grouping
    // below cannot pair a line with another line's amount. A part inside a
    // service reads $0 here because its money is in the service — the figures
    // add up to the template's total instead of overshooting it.
    const previewByRow = new Map(
      lineItems.map((li, index) => [li._rowId, lineItemPreviews[index]] as const),
    );
    const grouped = lineItems.reduce<Partial<Record<LineItemType, EditableLineItem[]>>>(
      (acc, li) => {
        const t = normalizeType(li.type);
        (acc[t] ??= []).push(li);
        return acc;
      },
      {},
    );
    const orderedGroups = TYPE_ORDER.filter((t) => grouped[t]?.length);

    return (
      <DetailPageLayout
        key={id}
        header={
          <>
            <div className="flex items-center gap-2 min-w-0">
              <Button
                variant="ghost"
                size="icon-sm"
                className="shrink-0 md:hidden"
                onClick={() => void router.navigate({ to: '/dashboard/settings/templates' })}
              >
                <ArrowLeft size={16} />
              </Button>
              <div className="min-w-0">
                <h2 className="text-base font-semibold truncate">{name || template?.name}</h2>
                {description && (
                  <p className="text-xs text-muted-foreground truncate">{description}</p>
                )}
              </div>
            </div>
            <TemplateActions
              id={id}
              name={name || template?.name || ''}
              kind="estimate"
              variant="header"
              onEdit={() => enterEdit()}
            />
          </>
        }
      >
        <Tabs
          value={tab}
          onValueChange={(t) =>
            void navigate({
              search: (prev) => ({ ...prev, tab: t as EstimateTemplateTab }),
              replace: true,
            })
          }
        >
          <TabsList
            variant="line"
            className="w-full justify-start border-b rounded-none px-0 h-auto pb-0"
          >
            <TabsTrigger value="overview">
              <FileText className="size-3.5" />
              Overview
            </TabsTrigger>
            <TabsTrigger value="lineitems">
              <ListOrdered className="size-3.5" />
              Line Items
              {lineItems.length > 0 && (
                <span className="ml-1 text-[10px] bg-muted rounded-full px-1.5 py-0.5">
                  {lineItems.length}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="notes">
              <Paperclip className="size-3.5" />
              Notes &amp; Docs
            </TabsTrigger>
          </TabsList>

          {/* ── Overview ───────────────────────────────────────────────── */}
          <TabsContent value="overview" className="mt-4 space-y-4">
            <SalesDocumentDetailsCard
              items={[
                { label: 'Created', value: formatDate(template?.createdAt ?? '') },
                { label: 'Updated', value: formatDate(template?.updatedAt ?? '') },
                { label: 'Line Items', value: lineItems.length },
                { label: 'Total', value: fmt(grandTotal) },
              ]}
            />
          </TabsContent>

          {/* ── Line Items ─────────────────────────────────────────────── */}
          <TabsContent value="lineitems" className="mt-4 space-y-4">
            {lineItems.length === 0 ? (
              <div className="rounded-xl border border-dashed p-8 text-center text-sm text-muted-foreground">
                No line items
              </div>
            ) : (
              <DetailCard
                label={`${lineItems.length} item${lineItems.length !== 1 ? 's' : ''} · ${fmt(grandTotal)}`}
              >
                <div className="space-y-4">
                  {orderedGroups.map((type) => {
                    const meta = TYPE_META[type];
                    const groupItems = grouped[type]!;
                    return (
                      <div key={type} className="space-y-1.5">
                        <span
                          className={`text-[10px] font-semibold uppercase tracking-widest inline-block rounded px-1.5 py-0.5 ${meta.badge}`}
                        >
                          {meta.label}
                        </span>
                        <div className="space-y-1">
                          {groupItems.map((li) => (
                            <div
                              key={lineIdentityKey(li)}
                              className={`flex items-center gap-3 rounded-md border-l-2 bg-muted/30 px-3 py-2 ${meta.accent}`}
                            >
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium leading-tight truncate">
                                  {li.name}
                                </p>
                                <div className="flex items-center gap-1.5 mt-0.5">
                                  <span className="text-[10px] text-muted-foreground font-mono">
                                    {li.code}
                                  </span>
                                  {li.isOptional && (
                                    <span className="text-[10px] rounded border px-1 text-muted-foreground">
                                      optional
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="shrink-0 text-right">
                                <p className="text-sm font-semibold tabular-nums">
                                  {fmt(previewByRow.get(li._rowId)?.lineSubtotal ?? 0)}
                                </p>
                                <p className="text-[10px] text-muted-foreground tabular-nums">
                                  {li.priceStrategy === 'DYNAMIC'
                                    ? `${li.estimatedHours ?? 0}h @ ${fmt(li.laborRate ?? 0)}/hr`
                                    : `×${li.quantity} @ ${fmt(li.basePrice)}`}
                                </p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </DetailCard>
            )}
          </TabsContent>

          {/* ── Notes & Files ──────────────────────────────────────────── */}
          <TabsContent value="notes" className="mt-4 space-y-4">
            <SalesDocumentNotesDocsSection
              notes={notes}
              notesLabel="Notes"
              attachmentsCount={attachments.length}
              renderAttachments={
                <TemplateAttachmentsSection
                  kind="estimate"
                  mode="editable"
                  templateId={id}
                  attachments={attachments}
                  readOnly
                />
              }
            />
          </TabsContent>
        </Tabs>
      </DetailPageLayout>
    );
  }

  // ── Edit mode ─────────────────────────────────────────────────────────────
  return (
    <FormPageLayout
      title="Edit Template"
      onBack={discardAndExit}
      actions={
        <>
          <Button variant="outline" size="sm" onClick={discardAndExit}>
            Cancel
          </Button>
          <Button size="sm" onClick={handleSave} disabled={updateMutation.isPending}>
            {updateMutation.isPending && <Loader2 size={14} className="mr-1.5 animate-spin" />}
            Save
          </Button>
        </>
      }
    >
      <div className="min-h-full bg-muted/40">
        <div className="mx-auto max-w-6xl space-y-3 px-4 py-5">
          <FormAccordionCard
            open={sec.details}
            header={
              <CollapsibleSectionHeader
                label="Details"
                open={sec.details}
                onToggle={() => toggle('details')}
                badge="required"
              />
            }
            bodyClassName="space-y-4"
          >
            <TemplateDetailsSection
              name={name}
              onNameChange={setName}
              placeholder="AC Replacement Template"
              businessUnitIds={businessUnitIds}
              onBusinessUnitIdsChange={setBusinessUnitIds}
            />
          </FormAccordionCard>

          <DocumentLineItemsCard
            description="Build the template from pricebook or custom items."
            lineItemsProps={{
              ...documentLinePickers({
                onAdd: lineState.addPickedLineItem,
                onAddConfiguredPart: lineState.addConfiguredServicePart,
                lineItems,
              }),
              lineItems,
              lineItemPreviews,
              // Driven by the template's own mode: choosing time & materials has
              // to switch the line editor into T&M rules, or the author can never
              // enter the hours that mode then requires.
              pricingMode,
              removeConfiguredPart: lineState.removeConfiguredServicePart,
              updateQuantity: lineState.updateQuantity,
              updateName: lineState.updateName,
              updateType: lineState.updateType,
              updateUnit: lineState.updateUnit,
              updateBasePrice: lineState.updateBasePrice,
              updateManualPriceOverride: lineState.updateManualPriceOverride,
              updatePriceStrategy: lineState.updatePriceStrategy,
              updateLaborRate: lineState.updateLaborRate,
              updateEstimatedHours: lineState.updateEstimatedHours,
              updateLaborCostLines: lineState.updateLaborCostLines,
              laborRateProfiles,
              updateDynamicPricingConfig: lineState.updateDynamicPricingConfig,
              updateDynamicMarkupProfile: lineState.updateDynamicMarkupProfile,
              updateDescription: lineState.updateDescription,
              updateComponentDisplayMode: lineState.updateComponentDisplayMode,
              updateMarkupPercent: lineState.updateMarkupPercent,
              updateCostBasis: lineState.updateCostBasis,
              updateTaxable: lineState.updateTaxable,
              updateTaxAccounting: lineState.updateTaxAccounting,
              updateMemberPricing: lineState.updateMemberPricing,
              onSaveLineToPricebook: lineState.linkLineToCatalogItem,
              updateChargeable: lineState.updateChargeable,
              updateHiddenFromCustomer: lineState.updateHiddenFromCustomer,
              toggleOptional: lineState.toggleOptional,
              toggleSelected: lineState.toggleSelected,
              removeLineItem: lineState.removeLineItem,
              onReorder: lineState.reorderLineItems,
              // A template is authored for many customers, so nothing here can
              // depend on one customer's membership.
              membershipEligibleRowIds: new Set<string>(),
              membershipInfo: null,
            }}
            costPanelProps={{ lineItems, pricingMode, subtotal, taxTotal, grandTotal }}
          />

          <div className="flex items-center gap-3">
            <div className="h-px flex-1 bg-border" />
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Optional
            </span>
            <div className="h-px flex-1 bg-border" />
          </div>

          <FormAccordionCard
            open={sec.defaults}
            header={
              <CollapsibleSectionHeader
                label="Document defaults"
                open={sec.defaults}
                onToggle={() => toggle('defaults')}
                badge="optional"
              />
            }
            bodyClassName="space-y-4"
          >
            {/* What every estimate built from this template starts with. A
                field left blank says nothing, and the estimate keeps its own
                default. */}
            <EstimateTemplateDefaultsSection
              value={defaults}
              storedValue={storedDefaults}
              tmUnderquotedServices={underquotedTMServices}
              onTMRepriceFromParts={() => lineState.applyPricingMode('TM_OVERRIDE')}
              onChange={(patch) => {
                setDefaults((prev) => ({ ...prev, ...patch }));
                // The T&M switch is not only a default the estimate inherits —
                // it is the mode THESE lines are edited and priced under
                // (`templateEditorPricingMode`). Left to the flag alone, a
                // service here is stored in the shape neither engine can price
                // from its parts, and every estimate built from this template
                // inherits it. Materializing on the switch is what the estimate
                // editor does with the same control (`applyPricingMode`).
                if (patch.pricingMode !== undefined) {
                  lineState.applyPricingMode(patch.pricingMode ?? 'STANDARD');
                }
              }}
            />
          </FormAccordionCard>

          <FormAccordionCard
            open={sec.notes}
            header={
              <CollapsibleSectionHeader
                label="Notes"
                open={sec.notes}
                onToggle={() => toggle('notes')}
                badge="optional"
                statusBadge={
                  description.trim() || notes.trim() || terms.termsMode !== 'DEFAULT'
                    ? 'Added'
                    : undefined
                }
              />
            }
            bodyClassName="space-y-4"
          >
            <div className="space-y-1">
              <Label className="text-xs">Description</Label>
              <Input
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Reusable lineup for standard replacements"
                className="h-8 text-sm"
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes for when this template should be used"
                rows={3}
                className="text-sm"
              />
            </div>
            {/* Cascades onto every estimate built from this template; the
                estimate can still override before it goes out. */}
            <TermsSelector value={terms} onChange={setTerms} />
          </FormAccordionCard>

          <FormAccordionCard
            open={sec.attachments}
            header={
              <CollapsibleSectionHeader
                label="Attachments"
                open={sec.attachments}
                onToggle={() => toggle('attachments')}
                badge="optional"
                statusBadge={attachments.length > 0 ? 'Added' : undefined}
              />
            }
            bodyClassName="space-y-4"
          >
            <TemplateAttachmentsSection
              kind="estimate"
              mode="editable"
              templateId={id}
              attachments={attachments}
            />
          </FormAccordionCard>
        </div>
      </div>
      <TemplateUnsavedChangesDialog
        open={blocker.status === 'blocked'}
        onStay={() => {
          if (blocker.status === 'blocked') blocker.reset();
        }}
        onDiscard={() => {
          if (blocker.status === 'blocked') blocker.proceed();
        }}
      />
    </FormPageLayout>
  );
}
